import {Button, Tooltip} from '@primer/react'
import {clsx} from 'clsx'
import {useId} from 'react'

import {
  constructReactionButtonDescription,
  constructReactionButtonLabel,
  EMOJI_MAP,
  type ReactionContent,
  type ReactionViewerGroup,
  summarizeReactors,
} from '../utils/ReactionGroups'
import styles from './ReactionButton.module.css'

type ReactionButtonProps = {
  reactionGroup: ReactionViewerGroup
  disabled?: boolean
  onReact: (reaction: ReactionContent, viewerHasReacted: boolean) => void
}

export function ReactionButton({reactionGroup, disabled, onReact}: ReactionButtonProps) {
  const {content, viewerHasReacted} = reactionGroup.reaction
  const {reactors, totalCount} = reactionGroup
  const id = useId()

  if (totalCount === 0) return null

  const reactorLogins = reactors.flatMap(reactor => (reactor.typeName !== 'Other' ? [reactor.login] : []))
  const description = constructReactionButtonDescription(content, viewerHasReacted)
  const label = constructReactionButtonLabel(content, totalCount)

  return (
    <Tooltip text={summarizeReactors(reactorLogins, totalCount)} type="description" direction="ne">
      <Button
        size="small"
        aria-label={label}
        role="switch"
        aria-checked={viewerHasReacted}
        leadingVisual={() => <>{EMOJI_MAP[content]}</>}
        onClick={() => onReact(content, viewerHasReacted)}
        inactive={disabled}
        className={clsx(styles.reactionToggleButton, viewerHasReacted && styles.reactionToggleButtonReacted)}
        aria-describedby={id}
      >
        <span className="sr-only" id={id}>
          {description}
        </span>
        {totalCount}
      </Button>
    </Tooltip>
  )
}
